import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getLiveShifts, type LiveShiftRow } from "@/lib/live-shifts.functions";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/venue/live")({
  head: () => ({
    meta: [
      { title: "Live Shift Dashboard — The Hospitality Reserve" },
      { name: "description", content: "Real-time view of who's clocked in, arrival times and shift status." },
    ],
  }),
  component: VenueLivePage,
});

const STATE_BADGE: Record<string, string> = {
  scheduled: "bg-muted text-foreground",
  clocked_in: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  running_late: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  completed: "bg-primary/15 text-primary",
  no_show: "bg-destructive/15 text-destructive",
};

function fmtTime(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function VenueLivePage() {
  const fetchLive = useServerFn(getLiveShifts);
  const [rows, setRows] = useState<LiveShiftRow[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const r = await fetchLive({ data: undefined });
    setRows(r);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30_000);
    const channel = supabase
      .channel("venue-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, () => refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "shifts" }, () => refresh())
      .subscribe();
    return () => { clearInterval(interval); supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-5xl mx-auto px-4 py-10 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold">Live shift dashboard</h1>
          <p className="text-muted-foreground mt-1">Updates every 30 seconds and on changes.</p>
        </header>
        {loading ? <p className="text-muted-foreground">Loading…</p> : rows.length === 0 ? (
          <p className="text-muted-foreground">No shifts running in the next 24 hours.</p>
        ) : (
          <div className="space-y-4">
            {rows.map((s) => (
              <article key={s.shift_id} className="border rounded-xl p-4 bg-card">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <h2 className="font-medium capitalize">{s.role_type.replaceAll("_"," ")}</h2>
                  <p className="text-sm text-muted-foreground">
                    {new Date(s.start_time).toLocaleString("en-GB")} → {fmtTime(s.end_time)} · {s.bookings.length}/{s.positions_needed} on shift
                  </p>
                </div>
                <ul className="mt-3 divide-y">
                  {s.bookings.map((b) => (
                    <li key={b.booking_id} className="py-2 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{b.full_name ?? "Bartender"}</p>
                        <p className="text-xs text-muted-foreground">
                          Arrived {fmtTime(b.actual_arrival_at ?? b.arrival_venue_confirmed_at ?? b.arrival_bartender_confirmed_at)}
                          {b.completed_at && ` · Completed ${fmtTime(b.completed_at)}`}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${STATE_BADGE[b.state]}`}>
                        {b.state.replaceAll("_"," ")}
                      </span>
                    </li>
                  ))}
                  {s.bookings.length === 0 && <li className="py-2 text-sm text-muted-foreground">No bookings yet.</li>}
                </ul>
                <div className="mt-3 text-sm">
                  <Link to="/venue/rebook/$shiftId" params={{ shiftId: s.shift_id }} className="text-primary underline">
                    Find a substitute →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
